import React, { useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { FilterMatchMode } from 'primereact/api';
import NewButton from '../../../../../components/NewButton/NewButton';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { formatDateOnly } from '../../../../../utils/dateUtils';

const AdministrationTable = ({ data, loading, onEdit, onNew, onPageChange, totalRecords, pageNumber, pageSize, onSearch, userList, onSort }) => {
    const { th } = useLanguage();
    const [globalFilterValue, setGlobalFilterValue] = useState('');
    const [filters, setFilters] = useState({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS }
    });
    const [sortField, setSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState(null);

    const handleSort = (e) => {
        setSortField(e.sortField);
        setSortOrder(e.sortOrder);
        if (onSort) {
            onSort(e.sortField, e.sortOrder);
        }
    };

    const handleExport = () => {
        // Excel export için data'yı hazırla
        const exportData = data.map((item) => {
            const userDisplay = item.fullName || '-';
            const userWithId = item.userId ? `${item.userId} - ${userDisplay}` : userDisplay;
            return {
                [th('systemAdministrator.user')]: userWithId,
                [th('systemAdministrator.startDate')]: item.startDate ? formatDateOnly(item.startDate) : '-',
                [th('systemAdministrator.endDate')]: item.endDate ? formatDateOnly(item.endDate) : '-',
                [th('systemAdministrator.userType')]: item.role || item.userType || '-',
                [th('systemAdministrator.description')]: item.description || '-',
                [th('systemAdministrator.mailSend')]: item.mailSend === 1 ? th('systemAdministrator.active') : item.mailSend === 0 ? th('systemAdministrator.inactive') : '-',
                [th('systemAdministrator.status')]: item.isActive === 1 ? th('systemAdministrator.active') : item.isActive === 0 ? th('systemAdministrator.inactive') : '-'
            };
        });

        // CSV formatına çevir
        const headers = Object.keys(exportData[0] || {});
        const csvContent = [headers.join(','), ...exportData.map((row) => headers.map((header) => `"${(row[header] || '').toString().replace(/"/g, '""')}"`).join(','))].join('\n');

        // BOM ekle (Türkçe karakterler için)
        const BOM = '\uFEFF';
        const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });

        // Dosyayı indir
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `${th('systemAdministrator.exportFileName')}_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const actionBodyTemplate = (rowData) => {
        const hasAssignment = rowData.hasAssignment;
        return (
            <div className="flex align-items-center justify-content-center gap-2">
                <Button
                    icon={hasAssignment ? 'pi pi-pencil' : 'pi pi-plus'}
                    rounded
                    text
                    severity="info"
                    onClick={() => onEdit(rowData)}
                    tooltip={hasAssignment ? th('common.edit') : th('systemAdministrator.newRecord')}
                    tooltipOptions={{ position: 'top' }}
                />
            </div>
        );
    };

    const userBodyTemplate = (rowData) => {
        return <span> {rowData.fullName || '-'}</span>;
    };
    const userTypeBodyTemplate = (rowData) => {
        return <span>{rowData.role || rowData.userType || '-'}</span>;
    };

    const statusBodyTemplate = (rowData) => {
        if (rowData.isActive === null || rowData.isActive === undefined) return <span>-</span>;
        const isActive = rowData.isActive === 1;
        return <span className={`p-badge ${isActive ? 'p-badge-success' : 'p-badge-danger'}`}>{isActive ? th('systemAdministrator.active') : th('systemAdministrator.inactive')}</span>;
    };

    const mailSendBodyTemplate = (rowData) => {
        if (rowData.mailSend === null || rowData.mailSend === undefined) return <span>-</span>;
        const mailSent = rowData.mailSend === 1;
        return <span className={`p-badge ${mailSent ? 'p-badge-success' : 'p-badge-danger'}`}>{mailSent ? th('systemAdministrator.active') : th('systemAdministrator.inactive')}</span>;
    };

    const dateBodyTemplateCustom = (rowData, field) => {
        if (!rowData || !rowData[field]) return '-';

        return <span>{formatDateOnly(rowData[field])}</span>;
    };

    const header = (
        <div className="flex justify-content-end align-items-center gap-2">
            <NewButton onClick={onNew} label={th('systemAdministrator.newRecord')} items={[]} className="p-button-sm" />
        </div>
    );

    return (
        <DataTable
            value={data}
            paginator
            showGridlines
            rows={pageSize}
            first={pageNumber * pageSize}
            totalRecords={totalRecords}
            onPage={(e) => onPageChange(e)}
            dataKey="rowKey"
            loading={loading}
            filters={filters}
            globalFilterFields={['fullName', 'userType', 'description']}
            header={header}
            emptyMessage={th('systemAdministrator.emptyMessage')}
            rowsPerPageOptions={[5, 10, 25, 50]}
            tableStyle={{ minWidth: '70rem' }}
            lazy
            sortField={sortField}
            sortOrder={sortOrder}
            onSort={handleSort}
            responsiveLayout="scroll"
        >
            <Column body={actionBodyTemplate} style={{ width: '5%' }} align="center" />
            <Column field="fullName" header={th('systemAdministrator.user')} body={userBodyTemplate} sortable style={{ minWidth: '18rem' }} />
            <Column field="startDate" header={th('systemAdministrator.startDate')} body={(rowData) => dateBodyTemplateCustom(rowData, 'startDate')} sortable style={{ minWidth: '12rem' }} />
            <Column field="endDate" header={th('systemAdministrator.endDate')} body={(rowData) => dateBodyTemplateCustom(rowData, 'endDate')} sortable style={{ minWidth: '12rem' }} />
            <Column field="role" header={th('systemAdministrator.userType')} body={userTypeBodyTemplate} sortable style={{ minWidth: '12rem' }} />
            <Column field="mailSend" header={th('systemAdministrator.mailSend')} body={mailSendBodyTemplate} sortable style={{ width: '8rem', textAlign: 'center' }} />
            <Column field="isActive" header={th('systemAdministrator.status')} body={statusBodyTemplate} sortable style={{ width: '10rem', textAlign: 'center' }} />
        </DataTable>
    );
};

export default AdministrationTable;
